import {Component, DestroyRef, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute} from '@angular/router';
import {MatCard, MatCardContent} from '@angular/material/card';
import {TranslocoModule} from '@jsverse/transloco';

// Story 50.1 (EO-302) — landed here by identity's /authorize when the request
// failed on one of the four non-redirectable branches (missing/unknown
// client_id, missing/unregistered redirect_uri; RFC 6749 §4.1.2.1). No client
// is known or trusted at that point, so there is no client_id to read, no
// branding to fetch (TenantConfigService is never injected) — on a tenant's
// custom domain the served page is theirs by HOST (Story 50.2), never by
// client. No controls, no links — retrying the same link cannot help, and a
// bare /login without a client would be a second dead end.
//
// Story 50.3 (EO-348) — the one thing that MAY vary is the sentence saying
// which of the four checks failed, from the `reason` query parameter identity
// puts on the Location. It is a closed vocabulary (REASON_KEYS); an unknown or
// absent value renders nothing extra, and the value itself is never echoed —
// only a catalog key chosen from it. The JSON envelope always named the
// branch to any non-browser caller, so saying it here discloses nothing new;
// the HOST rule is what protects tenant identity, and this page reads no host.
const REASON_KEYS: Record<string, string> = {
  missing_client_id: 'authorizationError.reasons.missing_client_id',
  invalid_client_id: 'authorizationError.reasons.invalid_client_id',
  missing_redirect_uri: 'authorizationError.reasons.missing_redirect_uri',
  invalid_redirect_uri: 'authorizationError.reasons.invalid_redirect_uri',
};

@Component({
  selector: 'app-authorization-error',
  standalone: true,
  imports: [MatCard, MatCardContent, TranslocoModule],
  templateUrl: './authorization-error.component.html',
  styleUrl: './authorization-error.component.css',
})
export class AuthorizationErrorComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  /** Catalog key for the reason sentence, or '' when there is none to show. */
  reasonKey = '';

  constructor() {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.reasonKey = REASON_KEYS[params['reason']] ?? '';
    });
  }
}
